import { useEffect, useState } from 'react';
import CheckCircleOutlined from '@ant-design/icons/CheckCircleOutlined';
import ExclamationCircleOutlined from '@ant-design/icons/ExclamationCircleOutlined';
import LoadingOutlined from '@ant-design/icons/LoadingOutlined';
import MessageOutlined from '@ant-design/icons/MessageOutlined';
import StopOutlined from '@ant-design/icons/StopOutlined';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import type { AnswerStatus } from './answerState';
import styles from './conversation.module.css';

/** The stage text: real service progress from the contract (§5). */
export const stageText = (
  t: TFunction,
  status: Extract<AnswerStatus, { kind: 'active' }>,
): string => {
  if (status.reconnecting) return t('v2.messages.st_reconnecting');
  if (status.phase === 'stopping') {
    if (status.stop === 'unconfirmed') return t('v2.messages.st_stopRequested');
    if (status.stop === 'failed') return t('v2.messages.st_stopFailed');
    return t('v2.messages.st_stopping');
  }
  const detail = status.detail ?? {};
  switch (status.stage) {
    case 'resolving_sources':
      return t('v2.messages.st_resolving', { n: detail.count ?? '…' });
    case 'retrieving_passages':
      return detail.count != null
        ? t('v2.messages.st_retrievingIn', { n: detail.count })
        : t('v2.messages.st_retrieving');
    case 'cross_checking':
      return detail.total != null
        ? t('v2.messages.st_crosscheckOf', {
            done: detail.done ?? 0,
            total: detail.total,
          })
        : t('v2.messages.st_crosscheck');
    case 'extracting_differences':
      return t('v2.messages.st_comparing');
    case 'generating':
      return t('v2.messages.st_generating');
    case 'resolving_citations':
      return t('v2.messages.st_citations');
    case 'waiting':
      return t('v2.messages.st_queued');
    default:
      return status.hasText
        ? t('v2.messages.st_generating')
        : t('v2.messages.st_queued');
  }
};

const Elapsed = ({ since }: { since: number }) => {
  const { t } = useTranslation();
  const seconds = () => Math.max(0, Math.round((Date.now() - since) / 1000));
  const [value, setValue] = useState(seconds);
  useEffect(() => {
    setValue(seconds());
    const timer = setInterval(() => {
      if (!document.hidden) setValue(seconds());
    }, 1000);
    return () => clearInterval(timer);
    // seconds reads `since` only.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [since]);
  return (
    <span className={styles.elapsed} data-testid="run-elapsed">
      {t('v2.common.elapsed', { s: value })}
    </span>
  );
};

/** One answer's status: spinner and stage while active, then how it ended. */
const RunStatusLine = ({ status }: { status: AnswerStatus }) => {
  const { t } = useTranslation();

  if (status.kind === 'active') {
    return (
      <div
        className={styles.status}
        data-testid="run-status"
        data-state={status.reconnecting ? 'reconnecting' : status.phase}
      >
        <LoadingOutlined className={styles.spinner} aria-hidden />
        <span role="status" className={styles.stage}>
          {stageText(t, status)}
        </span>
        {status.startedAt != null ? <Elapsed since={status.startedAt} /> : null}
      </div>
    );
  }

  let tone = 'quiet';
  let icon = <CheckCircleOutlined aria-hidden />;
  let text: string;
  switch (status.kind) {
    case 'completed':
      text =
        status.durationMs != null
          ? t('v2.messages.st_completed', {
              s: Math.max(1, Math.round(status.durationMs / 1000)),
            })
          : t('v2.messages.st_done');
      break;
    case 'awaiting_reply':
      icon = <MessageOutlined aria-hidden />;
      text = t('v2.messages.st_awaitingReply');
      break;
    case 'stopped':
      tone = 'warning';
      icon = <StopOutlined aria-hidden />;
      text = t('v2.messages.st_stopped');
      break;
    case 'failed': {
      tone = 'danger';
      icon = <ExclamationCircleOutlined aria-hidden />;
      const reason = status.error?.message || status.detail;
      text = reason
        ? `${t('v2.messages.st_failed')} — ${reason.replace(/<[^>]*>/g, '').trim()}`
        : t('v2.messages.st_failed');
      break;
    }
    case 'disconnected':
    case 'interrupted':
      tone = 'danger';
      icon = <ExclamationCircleOutlined aria-hidden />;
      text =
        status.stop === 'requested'
          ? t('v2.messages.st_stopping')
          : status.stop === 'unconfirmed'
            ? t('v2.messages.st_stopRequested')
            : status.stop === 'failed'
              ? t('v2.messages.st_stopFailed')
              : status.kind === 'disconnected'
                ? t('v2.messages.st_disconnectedKept')
                : t('v2.messages.st_interrupted');
      break;
    default:
      return null;
  }
  return (
    <div
      className={styles.status}
      data-testid="run-status"
      data-state={status.kind}
      data-tone={tone}
    >
      {icon}
      <span>{text}</span>
    </div>
  );
};

export default RunStatusLine;
